import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { NOTE_COLORS, colorOf } from '@/lib/noteColors';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, Trash2, ChevronDown, ChevronRight, CheckSquare, Square,
  StickyNote, Search, Type, List as ListIcon, ListChecks, Loader2, Lock,
  GripVertical, Share2, Copy, Check, Tag,
} from 'lucide-react';

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : 'id' + Math.random().toString(36).slice(2));
const newItem = (type = 'text') => ({ id: uid(), type, text: '', done: false });
const newSection = (title = '') => ({ id: uid(), title, collapsed: false, items: [newItem('text')] });
const starterSections = () => ([
  { id: uid(), title: 'Reminders', collapsed: false, items: [newItem('todo')] },
  { id: uid(), title: 'Notes', collapsed: false, items: [newItem('bullet')] },
]);

const timeAgo = (iso) => {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};
const snippet = (n) => {
  const t = (n.sections || []).flatMap((s) => s.items || []).map((i) => i.text).filter(Boolean).join(' · ');
  return t.slice(0, 60) || 'Empty note';
};

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [copied, setCopied] = useState(false);
  const timers = useRef({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    api.get('/notes')
      .then((r) => {
        setNotes(r.data);
        const want = new URLSearchParams(window.location.search).get('open');
        const pick = r.data.find((n) => n.id === want) || r.data[0];
        if (pick) setActiveId(pick.id);
      })
      .catch((e) => { if (e?.response?.status === 402) setLocked(true); })
      .finally(() => setLoading(false));
  }, []);

  const active = useMemo(() => notes.find((n) => n.id === activeId) || null, [notes, activeId]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return notes;
    return notes.filter((n) => (n.title + ' ' + snippet(n)).toLowerCase().includes(s));
  }, [notes, q]);

  const scheduleSave = (note) => {
    clearTimeout(timers.current[note.id]);
    timers.current[note.id] = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/notes/${note.id}`, { title: note.title, sections: note.sections, pinned: note.pinned, color: note.color ?? null });
      } catch { toast.error('Could not save note'); }
      finally { setSaving(false); }
    }, 700);
  };

  const applyToActive = (mutator) => {
    setNotes((prev) => prev.map((n) => {
      if (n.id !== activeId) return n;
      const updated = { ...n, ...mutator(n), updated_at: new Date().toISOString() };
      scheduleSave(updated);
      return updated;
    }));
  };

  const patchSection = (sid, patch) => applyToActive((n) => ({ sections: n.sections.map((s) => (s.id === sid ? { ...s, ...patch } : s)) }));
  const patchItem = (sid, iid, patch) => applyToActive((n) => ({
    sections: n.sections.map((s) => (s.id === sid ? { ...s, items: s.items.map((i) => (i.id === iid ? { ...i, ...patch } : i)) } : s)),
  }));
  const addItem = (sid, type) => applyToActive((n) => ({ sections: n.sections.map((s) => (s.id === sid ? { ...s, items: [...s.items, newItem(type)] } : s)) }));
  const delItem = (sid, iid) => applyToActive((n) => ({ sections: n.sections.map((s) => (s.id === sid ? { ...s, items: s.items.filter((i) => i.id !== iid) } : s)) }));
  const addSection = () => applyToActive((n) => ({ sections: [...n.sections, newSection('')] }));
  const delSection = (sid) => applyToActive((n) => ({ sections: n.sections.filter((s) => s.id !== sid) }));
  const setColor = (key) => applyToActive(() => ({ color: key }));

  const onDragEnd = (e) => {
    const { active: a, over } = e;
    if (!over || a.id === over.id || !active) return;
    const secIdx = active.sections.findIndex((s) => s.id === a.id);
    if (secIdx !== -1) {
      const overIdx = active.sections.findIndex((s) => s.id === over.id);
      if (overIdx !== -1) applyToActive((n) => ({ sections: arrayMove(n.sections, secIdx, overIdx) }));
      return;
    }
    for (const s of active.sections) {
      const oi = s.items.findIndex((i) => i.id === a.id);
      if (oi !== -1) {
        const ni = s.items.findIndex((i) => i.id === over.id);
        if (ni !== -1) applyToActive((n) => ({ sections: n.sections.map((x) => (x.id === s.id ? { ...x, items: arrayMove(x.items, oi, ni) } : x)) }));
        return;
      }
    }
  };

  const createNote = async () => {
    try {
      const { data } = await api.post('/notes', { title: 'Untitled note', sections: starterSections() });
      setNotes((prev) => [data, ...prev]);
      setActiveId(data.id);
    } catch (e) { if (e?.response?.status === 402) setLocked(true); else toast.error('Could not create note'); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    try {
      await api.delete(`/notes/${id}`);
      setNotes((prev) => {
        const rest = prev.filter((n) => n.id !== id);
        if (id === activeId) setActiveId(rest[0]?.id || null);
        return rest;
      });
    } catch { toast.error('Could not delete note'); }
  };

  const toggleShare = async () => {
    if (!active) return;
    try {
      if (active.shared) {
        await api.delete(`/notes/${active.id}/share`);
        setNotes((prev) => prev.map((n) => (n.id === active.id ? { ...n, shared: false } : n)));
        toast.message('Sharing stopped — the link no longer works');
      } else {
        const { data } = await api.post(`/notes/${active.id}/share`);
        setNotes((prev) => prev.map((n) => (n.id === active.id ? { ...n, shared: true, share_token: data.share_token } : n)));
        toast.success('Share link created');
      }
    } catch { toast.error('Could not update sharing'); }
  };

  const shareUrl = active?.share_token ? `${window.location.origin}/n/${active.share_token}` : '';
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { toast.error('Copy failed'); }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-slate-400 font-mono"><Loader2 className="animate-spin mx-auto mb-3" /> Loading notes…</div>;

  if (locked) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="notes-locked">
        <Lock className="mx-auto text-brand mb-4" size={40} />
        <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">Notes are a Pro feature</h1>
        <p className="text-slate-600 mt-2">Keep job checklists, reminders and site notes in one place. Start your 14-day free trial to unlock Notes.</p>
        <a href="/pricing" className="tm-btn inline-flex mt-5" data-testid="notes-upgrade-btn">See Pro plans</a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="notes-page">
      <Seo title="Notes" noindex />
      <Toaster position="top-center" richColors />

      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business</p>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-2">
            <StickyNote className="text-brand" size={30} /> Notes
          </h1>
        </div>
        <span className="font-mono text-xs text-slate-400 h-5 flex items-center gap-1">
          {saving && <><Loader2 size={12} className="animate-spin" /> Saving…</>}
        </span>
      </div>

      <div className="grid md:grid-cols-[290px_1fr] gap-5">
        {/* Sidebar */}
        <aside className="space-y-3">
          <button onClick={createNote} data-testid="notes-new-btn" className="tm-btn w-full justify-center inline-flex"><Plus size={16} /> New note</button>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes" data-testid="notes-search"
              className="w-full border border-slate-300 pl-9 pr-3 py-2 text-sm focus:border-brand outline-none" />
          </div>
          <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
            {filtered.length === 0 && <p className="text-slate-400 text-sm font-mono py-6 text-center">No notes yet.</p>}
            {filtered.map((n) => {
              const c = colorOf(n.color);
              return (
                <button key={n.id} onClick={() => setActiveId(n.id)} data-testid={`note-list-item-${n.id}`}
                  style={{ borderLeft: `5px solid ${c.bar === 'transparent' ? '#e2e8f0' : c.bar}` }}
                  className={`w-full text-left tm-card p-3 pl-3 transition-shadow ${n.id === activeId ? 'ring-2 ring-brand' : 'hover:shadow-lg'}`}>
                  <div className="flex items-center gap-2">
                    <p className="font-bold truncate flex-1">{n.title || 'Untitled note'}</p>
                    {n.shared && <Share2 size={13} className="text-brand shrink-0" />}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{snippet(n)}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] font-mono text-slate-400">{timeAgo(n.updated_at)}</span>
                    {n.color && <span className="text-[10px] font-mono px-1.5 rounded" style={{ color: c.dot }}>{c.label}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Editor */}
        <section>
          {!active ? (
            <div className="tm-card p-12 text-center text-slate-500" data-testid="notes-empty">
              <StickyNote size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="font-bold">Select a note, or create a new one.</p>
            </div>
          ) : (
            <div className="tm-card p-5 sm:p-6" data-testid="note-editor" style={{ borderTop: `5px solid ${colorOf(active.color).bar === 'transparent' ? '#0f172a' : colorOf(active.color).bar}` }}>
              <div className="flex items-start gap-3 pb-3">
                <input value={active.title} onChange={(e) => applyToActive(() => ({ title: e.target.value }))}
                  data-testid="note-title-input" placeholder="Note title"
                  className="flex-1 font-display font-semibold text-2xl uppercase tracking-tight outline-none bg-transparent" />
                <button onClick={() => deleteNote(active.id)} data-testid="note-delete-btn" title="Delete note"
                  className="text-slate-400 hover:text-rose-600 shrink-0 mt-1"><Trash2 size={20} /></button>
              </div>

              {/* Toolbar: colour label + share */}
              <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 pb-4 mb-4">
                <div className="flex items-center gap-1.5" data-testid="note-color-picker">
                  <Tag size={14} className="text-slate-400" />
                  {NOTE_COLORS.map((c) => (
                    <button key={c.label} onClick={() => setColor(c.key)} title={c.label} data-testid={`note-color-${c.key || 'none'}`}
                      className={`w-5 h-5 rounded-full border transition-transform hover:scale-110 ${(active.color ?? null) === c.key ? 'ring-2 ring-offset-1 ring-slate-900 scale-110' : 'border-white'}`}
                      style={{ backgroundColor: c.dot }} />
                  ))}
                  {active.color && <span className="text-[11px] uppercase tracking-wide text-slate-500 ml-1 font-semibold">{colorOf(active.color).label}</span>}
                </div>
                <button onClick={toggleShare} data-testid="note-share-btn"
                  className={`rounded-full ml-auto inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide border px-3 py-1.5 transition-colors ${active.shared ? 'bg-brand text-white border-brand' : 'border-slate-800 hover:bg-slate-900 hover:text-white'}`}>
                  <Share2 size={14} /> {active.shared ? 'Sharing on' : 'Share'}
                </button>
              </div>

              {active.shared && shareUrl && (
                <div className="mb-4 tm-card p-3 bg-slate-50 flex items-center gap-2 flex-wrap" data-testid="note-share-panel">
                  <input readOnly value={shareUrl} data-testid="note-share-url" className="flex-1 min-w-[180px] text-xs font-mono border border-slate-300 px-2 py-1.5 bg-white" onFocus={(e) => e.target.select()} />
                  <button onClick={copyLink} data-testid="note-share-copy" className="rounded-full inline-flex items-center gap-1 text-xs uppercase border border-slate-800 px-2 py-1.5 hover:bg-slate-900 hover:text-white font-semibold">
                    {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                  <button onClick={toggleShare} data-testid="note-share-stop" className="text-xs uppercase text-rose-600 hover:underline px-1 font-semibold">Stop sharing</button>
                  <p className="w-full text-[11px] text-slate-500">Anyone with this link can view this note (read-only). Tick boxes show as ticked/unticked but can't be changed.</p>
                </div>
              )}

              {/* Sections + items (draggable) */}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={active.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-4">
                    {active.sections.map((s) => (
                      <SortableSection key={s.id} section={s} onToggle={() => patchSection(s.id, { collapsed: !s.collapsed })}
                        onTitle={(v) => patchSection(s.id, { title: v })} onDelete={() => delSection(s.id)}>
                        <SortableContext items={s.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                          <div className="p-3 space-y-1.5">
                            {s.items.map((it) => (
                              <SortableItem key={it.id} item={it} sid={s.id}
                                onText={(v) => patchItem(s.id, it.id, { text: v })}
                                onToggleDone={() => patchItem(s.id, it.id, { done: !it.done })}
                                onDelete={() => delItem(s.id, it.id)} />
                            ))}
                            <div className="flex flex-wrap gap-1 pt-2 pl-6">
                              <IconBtn onClick={() => addItem(s.id, 'text')} tid={`add-text-${s.id}`} icon={<Type size={13} />}>Text</IconBtn>
                              <IconBtn onClick={() => addItem(s.id, 'bullet')} tid={`add-bullet-${s.id}`} icon={<ListIcon size={13} />}>Bullet</IconBtn>
                              <IconBtn onClick={() => addItem(s.id, 'todo')} tid={`add-todo-${s.id}`} icon={<ListChecks size={13} />}>Tick box</IconBtn>
                            </div>
                          </div>
                        </SortableContext>
                      </SortableSection>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <button onClick={addSection} data-testid="add-section-btn"
                className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest border border-slate-200 px-3 py-2 hover:bg-brand hover:text-white hover:border-brand transition-colors font-semibold">
                <Plus size={14} /> Add box section
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function SortableSection({ section, onToggle, onTitle, onDelete, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`section-${section.id}`} className="border border-slate-200 bg-white">
      <div className="flex items-center gap-1.5 bg-slate-50 border-b border-slate-200 px-2 py-2">
        <button {...attributes} {...listeners} data-testid={`section-drag-${section.id}`} className="text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing touch-none shrink-0" title="Drag to reorder"><GripVertical size={16} /></button>
        <button onClick={onToggle} data-testid={`section-collapse-${section.id}`} className="text-slate-500 hover:text-brand shrink-0">
          {section.collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
        </button>
        <input value={section.title} onChange={(e) => onTitle(e.target.value)} placeholder="Section title" data-testid={`section-title-${section.id}`}
          className="flex-1 text-xs uppercase tracking-widest font-bold text-slate-700 bg-transparent outline-none" />
        {section.collapsed && <span className="text-[10px] font-mono text-slate-400">{section.items.length} item{section.items.length !== 1 ? 's' : ''}</span>}
        <button onClick={onDelete} data-testid={`section-delete-${section.id}`} className="text-slate-300 hover:text-rose-600 shrink-0"><Trash2 size={15} /></button>
      </div>
      {!section.collapsed && children}
    </div>
  );
}

function SortableItem({ item, sid, onText, onToggleDone, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const grow = (el) => { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; };
  return (
    <div ref={setNodeRef} style={style} className="group flex items-start gap-1.5" data-testid={`item-${item.id}`}>
      <button {...attributes} {...listeners} data-testid={`item-drag-${item.id}`} className="text-slate-200 group-hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none shrink-0 mt-1.5" title="Drag to reorder"><GripVertical size={14} /></button>
      {item.type === 'todo' && (
        <button onClick={onToggleDone} data-testid={`item-check-${item.id}`} className={`shrink-0 mt-1 ${item.done ? 'text-emerald-600' : 'text-slate-400 hover:text-brand'}`}>
          {item.done ? <CheckSquare size={18} /> : <Square size={18} />}
        </button>
      )}
      {item.type === 'bullet' && <span className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-brand" />}
      <textarea value={item.text} rows={1}
        onChange={(e) => { onText(e.target.value); grow(e.target); }}
        onFocus={(e) => grow(e.target)}
        placeholder={item.type === 'todo' ? 'To-do…' : item.type === 'bullet' ? 'List item…' : 'Write here…'}
        data-testid={`item-text-${item.id}`}
        className={`flex-1 resize-none outline-none bg-transparent text-sm leading-relaxed py-0.5 ${item.done ? 'line-through text-slate-400' : 'text-slate-800'}`} />
      <button onClick={onDelete} data-testid={`item-delete-${item.id}`} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600 shrink-0 mt-1 transition-opacity"><Trash2 size={13} /></button>
    </div>
  );
}

function IconBtn({ onClick, tid, icon, children }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className="rounded-full inline-flex items-center gap-1 text-[11px] uppercase tracking-wide border border-slate-300 px-2 py-1 text-slate-600 hover:border-brand hover:text-brand transition-colors font-semibold">
      {icon} {children}
    </button>
  );
}
